"use strict";
/*
 * Copyright 2025, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
const core_1 = require("@deck.gl/core");
const gl_matrix_1 = require("gl-matrix");
const uniformBlock = `\
uniform animatedArrowPathUniforms {
  float time;
  float arrowSpacing;
} animatedArrowPath;
`;
const defaultProps = {
    time: { type: 'number', value: 0, min: 0, max: 1 },
    disableAnimation: { type: 'boolean', value: false }
};
class AnimatedArrowPathExtension extends core_1.LayerExtension {
    initializeState(context, extension) {
        var _a;
        (_a = this.getAttributeManager()) === null || _a === void 0 ? void 0 : _a.addInstanced({
            instanceStartOffsetRatios: {
                size: 1,
                accessor: 'getPath',
                transform: extension.getStartOffsetRatios.bind(this)
            },
            instanceLengthRatios: {
                size: 1,
                accessor: 'getPath',
                transform: extension.getLengthRatios.bind(this)
            }
        });
    }
    getStartOffsetRatios(path) {
        const result = [0];
        if (path === undefined || path.length < 2) {
            return result;
        }
        const positionSize = this.props.positionFormat === 'XY' ? 2 : 3;
        const isNested = Array.isArray(path[0]);
        const geometrySize = isNested ? path.length : path.length / positionSize;
        let sumLength = 0;
        let p;
        let prevP;
        for (let i = 0; i < geometrySize; i++) {
            p = isNested
                ? path[i]
                : path.slice(i * positionSize, i * positionSize + positionSize);
            p = this.projectPosition(p);
            if (i > 0 && prevP !== undefined) {
                const distance = gl_matrix_1.vec3.dist(prevP, p);
                if (i < geometrySize - 1) {
                    result[i] = result[i - 1] + distance;
                }
                sumLength += distance;
            }
            prevP = p;
        }
        for (let i = 0, count = result.length; i < count; i++) {
            result[i] = result[i] / sumLength;
        }
        return result;
    }
    getLengthRatios(path) {
        const result = [];
        if (path === undefined || path.length < 2) {
            return result;
        }
        const positionSize = this.props.positionFormat === 'XY' ? 2 : 3;
        const isNested = Array.isArray(path[0]);
        const geometrySize = isNested ? path.length : path.length / positionSize;
        let sumLength = 0;
        let p;
        let prevP = this.projectPosition(isNested ? path[0] : path.slice(0, positionSize));
        for (let i = 1; i < geometrySize; i++) {
            p = isNested
                ? path[i]
                : path.slice(i * positionSize, i * positionSize + positionSize);
            p = this.projectPosition(p);
            const distance = gl_matrix_1.vec3.dist(prevP, p);
            sumLength += distance;
            result[i - 1] = distance;
            prevP = p;
        }
        for (let i = 0, count = result.length; i < count; i++) {
            result[i] = result[i] / sumLength;
        }
        result.push(result[0]); // add last point again to make sure closed paths are handled
        return result;
    }
    draw(_params, _extension) {
        var _a, _b;
        const zoom = ((_a = this.context.viewport) === null || _a === void 0 ? void 0 : _a.zoom) || 14;
        // Arrow spacing in shader units - this is how far apart arrows are
        const arrowSpacing = this.props.arrowSpacing || 30.0;
        // Zoom factor calculation
        // Multiplier adjusts for low zoom being too fast visually
        const multiplier = (199 - 9 * zoom) / 19; // 10x slower at zoom 1, 1x at zoom 20
        // See for exact formula: https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
        const zoomFactor = multiplier * Math.pow(2, zoom - 15.6);
        // Original speed calculation to maintain correct visual speed
        const baseSpeed = 0.01; // Shader units per second before zoom adjustment, gives a good average speed
        const adjustedSpeed = baseSpeed / zoomFactor;
        // Fixed cycle duration - there will be a shift every 60 seconds when time wraps
        // The shift amount depends on zoom level and is generally acceptable for this use case
        const cycleDuration = 60.0; // seconds
        // Wrap time at the cycle duration to prevent overflow (performance.now() / 1000 gives seconds)
        const wrappedTime = (performance.now() / 1000) % cycleDuration;
        // Calculate distance traveled in this cycle - this is the animation time value
        const animationTime = this.props.disableAnimation ? 1 : wrappedTime * adjustedSpeed;
        const animatedArrowProps = {
            time: animationTime,
            arrowSpacing: arrowSpacing
        };
        const model = this.state.model;
        (_b = model === null || model === void 0 ? void 0 : model.shaderInputs) === null || _b === void 0 ? void 0 : _b.setProps({ animatedArrowPath: animatedArrowProps });
    }
    // See https://deck.gl/docs/developer-guide/custom-layers/picking for more information about picking colors
    getShaders() {
        const inject = {
            'vs:#decl': `
                in float instanceLengthRatios;
                in float instanceStartOffsetRatios;
                out float vLengthRatio;
                out float vStartOffsetRatio;
                out float vArrowPathOffset;
            `,
            'vs:#main-end': `
                vLengthRatio = instanceLengthRatios;
                vStartOffsetRatio = instanceStartOffsetRatios;
                vArrowPathOffset += animatedArrowPath.time / width.x;
            `,
            'fs:#decl': `
                in float vArrowPathOffset;
                in float vDistanceBetweenArrows;
                in float vStartOffsetRatio;
                in float vLengthRatio;
            `,
            'fs:#main-end': `
                float percentFromCenter = abs(vPathPosition.x);
                float offset = vArrowPathOffset;
                float totalLength = vPathLength / vLengthRatio;
                float startDistance = vStartOffsetRatio * totalLength;
                // percentFromCenter * 2.0 makes the arrow twice as pointy.
                float distanceSoFar = startDistance + vPathPosition.y - offset + percentFromCenter * 2.0;
                float arrowIndex = mod(distanceSoFar, animatedArrowPath.arrowSpacing);
                float percentOfDistanceBetweenArrows = 1.0 - arrowIndex / animatedArrowPath.arrowSpacing;
                
                // Create white border effect on the edges
                float borderWidth = 0.3; // Adjust this value to control border thickness
                float borderFactor = smoothstep(1.0 - borderWidth, 1.0, percentFromCenter);
                
                vec3 finalColor;
                if (percentOfDistanceBetweenArrows < 0.5) {
                    float percentBlack = percentOfDistanceBetweenArrows / 0.5 * 0.5;
                    finalColor = mix(vColor.rgb, vec3(0.0), percentBlack);
                } else if (percentOfDistanceBetweenArrows < 0.75) {
                    float percentWhite = (1.0 - (percentOfDistanceBetweenArrows - 0.5) * 4.0) * 0.75;
                    finalColor = mix(vColor.rgb, vec3(1.0), percentWhite);
                } else {
                    finalColor = vColor.rgb;
                }
                
                // Apply white border with antialiasing
                finalColor = mix(finalColor, vec3(1.0), borderFactor);
                
                // Required for events to work with picking: Apply deck.gl picking color filtering
                // This ensures that clicking works properly by allowing deck.gl to render picking colors
                // when in picking mode, and our custom colors when in normal rendering mode
                fragColor = picking_filterPickingColor(vec4(finalColor, 1.0));
            `
        };
        return {
            modules: [
                {
                    name: 'animatedArrowPath',
                    vs: uniformBlock,
                    fs: uniformBlock,
                    uniformTypes: {
                        time: 'f32',
                        arrowSpacing: 'f32'
                    },
                    inject
                }
            ]
        };
    }
}
AnimatedArrowPathExtension.extensionName = 'AnimatedArrowPathExtension';
AnimatedArrowPathExtension.layerName = 'AnimatedArrowPathLayer';
AnimatedArrowPathExtension.defaultProps = defaultProps;
exports.default = AnimatedArrowPathExtension;
//# sourceMappingURL=AnimatedArrowPathExtension.js.map